"use client";
import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { usePathname } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import logo from "../../public/assets/images/neoverce.png";
import CommonButton from "./CommonButton";
import { event } from "@/lib/analytics";
import ExitPageModal from "./ExitPageModal";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Blog", href: "/blog" },
  {
    label: "Services",
    href: "/services",
    megaMenu: true,
    categories: [
      {
        title: "FrontEnd",
        description: "Crafting Engaging and Responsive User Interfaces",
        items: ["React", "Angular", "Vue.JS", "Next.js", ".NET", "HTML/CSS"],
        link: "/services/frontend",
      },
      {
        title: "BackEnd",
        description: "Powering Scalable and Secure Applications",
        items: ["Node.Js", "NestJS", "Python", ".NET", "Express", "Golang"],
        link: "/services/backend",
      },
      {
        title: "Database",
        description: "Optimized Data Management for Peak Performance",
        items: [
          "PostgreSQL",
          "MongoDB",
          "MySQL",
          "Firebase",
          "SQLite",
          "Supabase",
        ],
        link: "/services/database",
      },
      {
        title: "UI/UX Design",
        description: "Seamless Designs for Exceptional User Experiences",
        items: ["Figma", "Adobe XD", "Photoshop", "Sketch", "Balsamiq"],
        link: "/services/uiux-design",
      },
      {
        title: "Mobile App Development",
        description: "Innovative Mobile Solutions for a Connected World",
        items: ["Flutter", "React Native", "Kotlin", "Android", "Swift"],
        link: "/services/mobile-app-development",
      },
      {
        title: "Cloud Services",
        description: "Scalable and Secure Cloud Solutions for Businesses",
        items: ["AWS EC2", "AWS S3", "AWS Lambda", "Microsoft Azure", "GCP"],
        link: "/services/cloud-services",
      },
    ],
  },
  { label: "Portfolio", href: "/portfolio" },
  { label: "Career", href: "/career" },
  { label: "Pricing", href: "/pricing" },
];

const Header = () => {
  const [showHeader, setShowHeader] = useState(true);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [lastScrollY, setLastScrollY] = useState(0);

  const [exitIntentLink, setExitIntentLink] = useState(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [exitModalCount, setExitModalCount] = useState(0);
  const [activeAccordion, setActiveAccordion] = useState(null); // for Services
  const [activeNestedAccordion, setActiveNestedAccordion] = useState(null); // for Frontend, Backend etc.
  const pathname = usePathname();
  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY;
      setShowHeader(!(current > lastScrollY && current > 100));
      setLastScrollY(current);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    if (pathname === "/pricing") {
      setExitModalCount(0);
    }
  }, [pathname]);

  const handleNavClick = (e, href) => {
    if (pathname === "/pricing" && href !== "/pricing") {
      if (exitModalCount < 2) {
        e.preventDefault();
        setExitIntentLink(href);
        setShowExitModal(true);
        setExitModalCount((prev) => prev + 1);
      }
    }
  };
  const confirmExit = () => {
    setShowExitModal(false);
    if (exitIntentLink) {
      window.location.href = exitIntentLink;
    }
  };
  return (
    <>
      <header className={`header  ${showHeader ? "show" : "hide"}`}>
        <Container
          className="header-main py-4 py-lg-2 px-4 d-flex align-items-center"
          fluid
        >
          <Link
            href="/"
            passHref
            className="p-0"
            aria-label={`navigate to home page`}
          >
            <Image
              src={logo}
              alt="Neoverce"
              className="img-fluid neoverce-logo"
              width={150}
              height={50}
              priority
            />
          </Link>
          <div className="d-lg-none ms-auto">
            <input
              id="checkbox"
              type="checkbox"
              style={{ display: "none" }}
              checked={isSidebarOpen}
              onChange={() => setIsSidebarOpen((prev) => !prev)}
            />
            <label htmlFor="checkbox" className="toggle">
              <div className="bar bar--top"></div>
              <div className="bar bar--middle"></div>
              <div className="bar bar--bottom"></div>
            </label>
          </div>

          <nav className="d-none  d-lg-flex align-items-center gap-lg-3 gap-xl-3 w-100 position-relative">
            {navLinks.map(({ label, href, megaMenu, categories }, index) => {
              const isLast = index === navLinks.length - 1;

              return (
                <div
                  key={index}
                  className="nav-item-wrapper"
                  onMouseEnter={() => megaMenu && setHoveredMenu(label)}
                  onMouseLeave={() => megaMenu && setHoveredMenu(null)}
                >
                  <Link
                    href={href}
                    onClick={(e) => handleNavClick(e, href)}
                    aria-label={`view ${label} page`}
                    className={`link-main nav-link d-flex align-items-center gap-2 ${
                      label === "Services"
                        ? pathname.includes("/services")
                          ? "active"
                          : ""
                        : pathname === href
                        ? "active"
                        : ""
                    }`}
                  >
                    {label}
                    {isLast && (
                      <Image
                        height={25}
                        width={25}
                        src="/star.png"
                        alt="Price"
                      />
                    )}
                  </Link>

                  {megaMenu && hoveredMenu === label && (
                    <div className="mega-menu p-4 position-absolute shadow bg-white">
                      <div className="row g-4">
                        {categories.map((cat, idx) => (
                          <div className="col-md-6" key={idx}>
                            <div className="mega-head">
                              <div className="fw-bold">{cat.title}</div>
                              <div className="text-muted small">
                                {cat.description}
                              </div>
                            </div>
                            <ul className="list-unstyled m-0">
                              {cat.items.map((tech, i) => (
                                <li key={i} className="small text-dark">
                                  {tech}
                                </li>
                              ))}
                            </ul>
                            <Link
                              href={cat.link}
                              className="view-all-link small text-primary"
                              onClick={() => setHoveredMenu(null)}
                              aria-label={`view ${label} page`}
                            >
                              View All →
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <Link
              href="/contact"
              className="ms-auto p-0"
              aria-label={`view contact us page`}
            >
              <CommonButton
                label="Contact us"
                className="btn-primary "
                onClick={() =>
                  event({
                    action: "free consultation",
                    params: { page: "header section" },
                  })
                }
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                    style={{ height: "20px" }}
                  >
                    <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" />
                  </svg>
                }
              />
            </Link>
          </nav>
        </Container>
      </header>
      {/* Sidebar overlay */}
      <div className={`sidebar ${isSidebarOpen ? "open" : ""}`}>
        <nav className="sidebar-nav d-flex flex-column px-3">
          {navLinks.map(({ label, href, megaMenu, categories }, i) => (
            <div key={i} className="sidebar-item mb-2">
              {!megaMenu ? (
                <Link
                  href={href}
                  aria-label={`view ${label} page`}
                  className={`py-2 d-block ${
                    label === "Services"
                      ? pathname.includes("/services")
                        ? "active"
                        : ""
                      : pathname === href
                      ? "active"
                      : ""
                  }`}
                  onClick={() => setIsSidebarOpen(false)}
                >
                  {label}
                </Link>
              ) : (
                <>
                  <div
                    className="py-2 fw-bold d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer", fontSize: "18px" }}
                  >
                    <Link
                      href="/services"
                      aria-label={`view services page`}
                      onClick={() => setIsSidebarOpen(false)}
                    >
                      {label}
                    </Link>
                    <span
                      onClick={() =>
                        setActiveAccordion((prev) =>
                          prev === label ? null : label
                        )
                      }
                    >
                      {activeAccordion === label ? "−" : "+"}
                    </span>
                  </div>
                  {activeAccordion === label && (
                    <div className="accordion-content">
                      {categories?.map((cat, j) => (
                        <div key={j} className="mb-2">
                          <div
                            className="d-flex justify-content-between align-items-center small fw-semibold nest-acc-head"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              setActiveNestedAccordion((prev) =>
                                prev === cat.title ? null : cat.title
                              )
                            }
                          >
                            {cat.title}
                            <span>
                              {activeNestedAccordion === cat.title ? "−" : "+"}
                            </span>
                          </div>

                          {activeNestedAccordion === cat.title && (
                            <div className="nested-grid mb-3">
                              <ul className="list-unstyled small mb-1">
                                {cat.items.map((item, k) => (
                                  <li key={k}>{item}</li>
                                ))}
                              </ul>
                              <Link
                                href={cat.link}
                                className="text-primary small"
                                onClick={() => setIsSidebarOpen(false)}
                                aria-label={`view ${cat.title} page`}
                              >
                                View All →
                              </Link>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          <Link
            href="/contact"
            className="w-100"
            aria-label={`view contact us page`}
          >
            <CommonButton
              label="Contact us"
              className="btn-primary w-100 "
              onClick={() => {
                event({
                  action: "free consultation",
                  params: { page: "header section" },
                });
                setIsSidebarOpen(false);
              }}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  style={{ height: "20px" }}
                >
                  <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" />
                </svg>
              }
            />
          </Link>
        </nav>
      </div>

      {/* Sidebar backdrop */}
      {isSidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      <ExitPageModal
        show={showExitModal}
        onHide={() => setShowExitModal(false)}
      />
    </>
  );
};

export default Header;
